import React, { useEffect, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { Col, Spinner } from "reactstrap";
import classNames from "classnames";
import FormGenerator from "components/customs/form-generator/input";
import CustomSelect from "components/customs/dropdown";
import { useNavigate } from "react-router";
import { addProjectDropdownStyles } from "constants/style";
import {
  FormActionsSection,
  FormRow,
  RequiredIndicator,
  StyledCancelButton,
  StyledSaveButton,
} from "assets/js/Styles";
import * as Yup from "yup";
import useProvinces from "hooks/useProvinces";
import useMunicipalities from "hooks/useMunicipalities";

const validSchema = Yup.object().shape({
  province: Yup.object().required("Required"),
  municipality: Yup.object().required("Required"),
  region: Yup.object().required("Required"),
  contact: Yup.string()
    .min(11, "At least 11 digits")
    .max(11, "Cannot be more than 11 digits")
    .matches(/^(09)\d{9}$/, "Invalid format")
    .required("Required"),
  name: Yup.object().required("Required"),
});

const DirectoryForm = ({
  handleOnSubmit,
  formFields,
  setFormFields,
  governorOptions,
  regionOptions,
  partylists,
  selectedPartylist,
  setSelectedPartylist,
  partylistError,
  isLoading,
  isEdit,
  position,
}) => {
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [municipalityOptions, setMunicipalityOptions] = useState([]);

  const navigate = useNavigate();

  const { data: provinces } = useProvinces(formFields.region?.value);

  const { data: municipalities } = useMunicipalities(
    formFields.province?.value
  );

  const fetchAllProvinces = () => {
    if (provinces?.length) {
      const options = provinces.map(({ id, name, omegaId }) => ({
        label: name,
        value: id,
        omegaId,
      }));

      setProvinceOptions(options);

      const option = options?.find((op) => op.value === formFields?.provinceId);

      if (option) {
        setFormFields((prev) => ({
          ...prev,
          province: option,
        }));
      } else {
        setFormFields((prev) => ({
          ...prev,
          province: options[0],
        }));
      }
    } else {
      setProvinceOptions([]);
    }
  };

  const fetchAllMunicipalities = () => {
    if (municipalities?.length) {
      const options = municipalities.map(({ id, name, omegaId }) => ({
        label: name,
        value: id,
        omegaId,
      }));

      options.unshift({
        label: "All Municipalities",
        value: "All Municipalities",
      });

      setMunicipalityOptions(options);

      const option = options?.find(
        (op) => op.value === formFields?.municipalityId
      );

      if (option) {
        setFormFields({
          ...formFields,
          municipality: option,
        });
      } else {
        setFormFields({
          ...formFields,
          municipality: options[0],
        });
      }
    } else {
      setMunicipalityOptions([]);
    }
  };

  useEffect(() => {
    if (formFields.region && provinces?.length) {
      fetchAllProvinces();
    }
  }, [formFields.region, provinces?.length]);

  useEffect(() => {
    if (formFields.province && municipalities?.length) {
      fetchAllMunicipalities();

      if (formFields.province.value === "All Provinces") {
        const allMunicipalities = {
          label: "All Municipalities",
          value: "All Municipalities",
        };

        // Directly update formFields with the new municipality value
        setFormFields((prevFields) => ({
          ...prevFields,
          municipality: allMunicipalities,
        }));
      }
    }
  }, [formFields.province, municipalities?.length]);

  return (
    <Formik
      validationSchema={validSchema}
      onSubmit={handleOnSubmit}
      initialValues={formFields}
      enableReinitialize
    >
      {({ handleChange, values }) => (
        <Form>
          <Field name="name">
            {({ form: { touched, errors, setFieldValue } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"Name"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <CustomSelect
                    placeholder=""
                    name="name"
                    styles={addProjectDropdownStyles}
                    options={governorOptions}
                    className={classNames({
                      "is-invalid": errors?.name && touched?.name,
                    })}
                    value={values.name}
                    onChange={(option) => {
                      setFieldValue("name", option);

                      setFormFields({
                        ...formFields,
                        name: option,
                        lastName: option?.candidate?.last_name ?? "",
                        firstName: option?.candidate?.first_name ?? "",
                        fullName: option.label,
                        omegaId: option.value,
                      });
                    }}
                    isDisabled={isEdit}
                  />
                  <ErrorMessage name="name">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="contact">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"Contact Number"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    name="contact"
                    type="mobile"
                    className={classNames({
                      "is-invalid": errors?.contact && touched?.contact,
                    })}
                    value={values.contact || ""}
                    onChange={(value) => {
                      handleChange(value);

                      setFormFields({
                        ...formFields,
                        contact: value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="contact">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="region">
            {({ form: { touched, errors, setFieldValue } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"Implementing Agency"}
                    {RequiredIndicator}
                  </h5>
                </Col>

                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <CustomSelect
                    placeholder=""
                    styles={addProjectDropdownStyles}
                    options={regionOptions}
                    name="region"
                    className={classNames({
                      "is-invalid": errors?.region && touched?.region,
                    })}
                    onChange={(option) => {
                      setFieldValue("region", option);

                      setFormFields({
                        ...formFields,
                        region: option,
                      });
                    }}
                    value={values.region}
                  />
                  <ErrorMessage name="region">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="province">
            {({ form: { touched, errors, setFieldValue } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"Province"}
                    {RequiredIndicator}
                  </h5>
                </Col>

                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <CustomSelect
                    placeholder=""
                    styles={addProjectDropdownStyles}
                    options={provinceOptions}
                    name="province"
                    className={classNames({
                      "is-invalid": errors?.province && touched?.province,
                    })}
                    onChange={(option) => {
                      setFieldValue("province", option);

                      setFormFields({
                        ...formFields,
                        province: option,
                      });
                    }}
                    value={values.province}
                  />
                  <ErrorMessage name="province">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="municipality">
            {({ form: { touched, errors, setFieldValue } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"Municipality"}
                    {RequiredIndicator}
                  </h5>
                </Col>

                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <CustomSelect
                    placeholder=""
                    styles={addProjectDropdownStyles}
                    options={municipalityOptions}
                    name="municipality"
                    className={classNames({
                      "is-invalid":
                        errors?.municipality && touched?.municipality,
                    })}
                    onChange={(option) => {
                      setFieldValue("municipality", option);

                      setFormFields({
                        ...formFields,
                        municipality: option,
                      });
                    }}
                    value={values.municipality}
                    isDisabled={!values.province}
                  />
                  <ErrorMessage name="municipality">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <FormRow>
            <Col
              xs="12"
              sm="6"
              md="12"
              lg="6"
            >
              <h5>{"Party"}</h5>
            </Col>
            <Col
              xs="12"
              sm="6"
              md="12"
              lg="6"
            >
              <CustomSelect
                placeholder=""
                name="party"
                styles={addProjectDropdownStyles}
                options={partylists}
                value={selectedPartylist}
                onChange={(option) => setSelectedPartylist(option)}
              />
              <small className="field-error text-danger">
                {partylistError}
              </small>
            </Col>
          </FormRow>

          <Field name="cosFirstName">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>{"Chief of Staff First Name"}</h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="text"
                    name="cosFirstName"
                    className={classNames({
                      "is-invalid":
                        errors?.cosFirstName && touched?.cosFirstName,
                    })}
                    value={values.cosFirstName}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        cosFirstName: evt.target.value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="cosFirstName">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="cosLastName">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>{"Chief of Staff Last Name"}</h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="text"
                    name="cosLastName"
                    className={classNames({
                      "is-invalid": errors?.cosLastName && touched?.cosLastName,
                    })}
                    value={values.cosLastName}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        cosLastName: evt.target.value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="cosLastName">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="cosNumber">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>{"Contact Number"}</h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="mobile"
                    name="cosNumber"
                    className={classNames({
                      "is-invalid": errors?.cosNumber && touched?.cosNumber,
                    })}
                    value={values.cosNumber || ""}
                    onChange={(value) => {
                      handleChange(value);

                      setFormFields({
                        ...formFields,
                        cosNumber: value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="cosNumber">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <FormActionsSection>
            <StyledCancelButton
              outline
              disabled={isLoading}
              onClick={() => {
                if (position) {
                  navigate("/directory", {
                    state: { view: position },
                  });
                } else {
                  navigate("/directory");
                }
              }}
            >
              {"Cancel"}
            </StyledCancelButton>

            <StyledSaveButton
              color="primary"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <Spinner
                  animation="border"
                  color="light"
                  size="sm"
                />
              ) : (
                "Save"
              )}
            </StyledSaveButton>
          </FormActionsSection>
        </Form>
      )}
    </Formik>
  );
};

export default DirectoryForm;
